import React, {useContext, useState} from "react";
import {useTranslation} from "react-i18next";
import {LayoutContext} from "../../hooks/layout-context";
import {Link} from "react-router-dom";
import {Employee} from "../../types/employee";
import useEmployee from "../../hooks/use-employee";
import DataTable from "react-data-table-component";
import {Dropdown} from "react-bootstrap";
import Confirmation from "../../components/layout/confirmation";
import {generate} from "../../lib/random-id-lib";
import {ToastsContext} from "../../hooks/toasts-context";
import {UserContext} from "../../hooks/user-context";

export default function EmployeeList() {
  const {t} = useTranslation();
  const {layout, setLayout} = useContext(LayoutContext);
  const [rows, setRows] = useState<Array<Employee>>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [showTable, setShowTable] = useState(false);
  const {listing, updateEmployee, forceDeleteEmployee} = useEmployee();
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedEmployeeDelete, setSelectedEmployeeDelete] = useState<Employee | null>(null);
  const {toasts, setToasts} = useContext(ToastsContext);
  const {user, isLoading} = useContext(UserContext);
  const [search, setSearch] = useState<string>("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [configLoaded, setConfigLoaded] = useState<boolean>(false);
  const [defaultPage, setDefaultPage] = useState<number>(1);

  React.useEffect(() => {
    layout.sideMenu = "employee";
    layout.showSearch = true;
    layout.breadcrumbs = [{path: null, label: t("Użytkownicy")}];
    setLayout({...layout});
  }, []);

  React.useEffect(() => {
    if (configLoaded) {
      if (layout.topSearch !== search) {
        setSearch(layout.topSearch);
      }
    }
  }, [layout.topSearch]);

  React.useEffect(() => {
    const searchControl = (document.querySelector('#top-search') as HTMLInputElement);
    if (searchControl !== undefined && searchControl !== null) {
      if (search !== undefined && search !== null && search.length > 0 && search !== 'undefined' && search !== 'null') {
        setTimeout(() => {
          searchControl.value = search;
          searchControl.setAttribute('value', search);
        });
      }
    }
  }, [search, layout.showSearch]);

  const columns: Array<any> = [
    {
      name: "",
      selector: (row: any) =>
        row.pathThumb !== undefined && row.pathThumb !== null ? (
          <span className={"user-avatar"}>
            <img src={row.pathThumb} />
          </span>
        ) : (
          <span className={"user-avatar"}>{row.initial}</span>
        ),
      maxWidth: "50px",
      minWidth: "50px",
      right: false,
    },
    {
      name: t("Nazwa"),
      sortable: true,
      key: "firstName",
      selector: (row: any) => <span>{row.firstName}</span>,
      right: false,
    },
    {
      name: t("Email"),
      sortable: true,
      key: "email",
      selector: (row: any) => <span>{row.email}</span>,
      right: false,
    },
    {
      name: t("Oddział"),
      sortable: true,
      key: "partner",
      selector: (row: any) =>
        <span>{row.Partner !== undefined && row.Partner !== null ? `${row.Partner.title} (${row.Partner.city} ${row.Partner.street})` : ''}</span>,
      right: false,
    },
    {
      name: "",
      selector: (row: any) => (
        <Dropdown>
          <Dropdown.Toggle as={"a"}>
            <i className="mdi mdi-dots-vertical"></i>
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item as={"div"}>
              <Link to={`/employee/edit/${row.id}`}>{t("Edytuj")}</Link>
            </Dropdown.Item>
            {row.active ? (
              <Dropdown.Item onClick={() => setSelectedEmployee(row)}>{t("Zablokuj")}</Dropdown.Item>
            ) : (
              <Dropdown.Item onClick={() => setSelectedEmployee(row)}>{t("Odblokuj")}</Dropdown.Item>
            )}
            <Dropdown.Item onClick={() => setSelectedEmployeeDelete(row)}>{t("Usuń")}</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ),
      right: true,
    },
  ];

  const handlePageChange = async (page: number) => {
    setLoading(true);
    await loadListing(page, perPage);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setLoading(true);
    await loadListing(page, newPerPage);
    setPerPage(newPerPage);
  };

  const loadListing = async (
    page: number,
    currentPerPage: number,
    sField: string | null = null,
    sDirection: string | null = null,
    sSearch: string | null = null
  ) => {
    try {
      localStorage.setItem('table_employee', JSON.stringify({
        page, currentPerPage, sField, sDirection, search
      }));
      const result = await listing(page, currentPerPage, sSearch === undefined || sSearch === null ? search : sSearch, sField, sDirection);
      setRows(result.data);
      setTotalRows(result.page.totalElements);
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
    if (!showTable) {
      setShowTable(true);
    }
  };

  const deleteEmployee = async (id: any) => {
    if (selectedEmployeeDelete !== undefined && selectedEmployeeDelete !== null) {
      try {
        await forceDeleteEmployee(selectedEmployeeDelete.id!!);
        toasts.push({
          title: t("Sukces"),
          content: t("Użytkownik usunięty"),
          show: true,
          id: generate(32),
          type: "success",
        });
        setToasts([...toasts]);
      } catch (e) {
        console.log(e);
        toasts.push({
          title: t("Błąd"),
          content: t("Nie udało się usunąć"),
          show: true,
          id: generate(32),
          type: "danger",
        });
        setToasts([...toasts]);
      }

      await loadListing(1, perPage);
    }

    setSelectedEmployeeDelete(null);
  };

  React.useEffect(() => {
    (async () => {
      if (!configLoaded) {
        try {
          let tableConfig: any = localStorage.getItem('table_employee');
          if (tableConfig !== undefined && tableConfig !== null && tableConfig.length > 0) {
            tableConfig = JSON.parse(tableConfig);
            setSortDirection(tableConfig.sDirection);
            setSortField(tableConfig.sField);
            setPerPage(tableConfig.currentPerPage);
            setConfigLoaded(true);
            setDefaultPage(tableConfig.page);
            setSearch(tableConfig.search);
            const searchControl = (document.querySelector('#top-search') as HTMLInputElement);
            if (searchControl !== undefined && searchControl !== null) {
              if (tableConfig.search !== undefined && tableConfig.search !== null && tableConfig.search.length > 0 && tableConfig.search !== 'undefined' && tableConfig.search !== 'null') {
                setTimeout(() => {
                  searchControl.value = tableConfig.search;
                  searchControl.setAttribute('value', tableConfig.search);
                });
              }
            }
            if (tableConfig.search === undefined || tableConfig.search === null || tableConfig.search.length === 0) {
              await loadListing(tableConfig.page, tableConfig.currentPerPage, tableConfig.sField, tableConfig.sDirection, tableConfig.search);
            }
          } else {
            setConfigLoaded(true);
            await loadListing(1, perPage, sortField, sortDirection, search);
          }
        } catch (e) {
          console.log(e);
        }
      } else {
        await loadListing(1, perPage, sortField, sortDirection, search);
      }
    })();
  }, [search]);

  const changeEmployeeState = async () => {
    if (selectedEmployee !== undefined && selectedEmployee !== null) {
      try {
        await updateEmployee(selectedEmployee.id!!, {active: !selectedEmployee.active});
        toasts.push({title: t("Sukces"), content: t("Dane zapisane"), show: true, id: generate(32), type: "success"});
        setToasts([...toasts]);
      } catch (e) {
        console.log(e);
        toasts.push({
          title: t("Błąd"),
          content: t("Nie udało się zapisać"),
          show: true,
          id: generate(32),
          type: "danger",
        });
        setToasts([...toasts]);
      }

      try {
        const index = rows.findIndex((it) => it.id === selectedEmployee.id!!);
        if (index !== -1) {
          rows[index].active = !rows[index].active;
        }
        setRows([...rows]);
      } catch (e) {
        console.log(e);
      }
    }

    setSelectedEmployee(null);
  };

  const handleSort = async (column: any, direction: any) => {
    setLoading(true);
    setSortField(column.key);
    setSortDirection(direction);
    await loadListing(1, perPage, column.key, direction);
    setLoading(false);
  };

  return (
    <div className="row mb-5">
      <div className="col-12">
        <div className="card shadow">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="header-title">{t('Użytkownicy')}</h4>
              {!isLoading &&
              user !== undefined &&
              user !== null &&
              user.permissions.indexOf("employee.add") !== -1 ? (
                <Link to={"/employee/add"} className="btn btn-light mb-2">
                  <i className="mdi mdi-plus-circle me-2"></i>
                  {t("Dodaj użytkownika")}
                </Link>
              ) : (
                <></>
              )}
            </div>
            <div className="table-responsive">
              {showTable ? (
                <DataTable
                  conditionalRowStyles={[{when: (row) => !row.active, classNames: ["row-disabled"]}]}
                  columns={columns}
                  data={rows}
                  striped={false}
                  sortServer
                  onSort={handleSort}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30, 60, 90, 120, 150]}
                  noDataComponent={<div className={"p5"}>{t("Brak użytkowników do wyświetlenia")}</div>}
                  paginationComponentOptions={{
                    rowsPerPageText: t("Wyświetl użytkowników na stronę:"),
                    rangeSeparatorText: t("of"),
                    noRowsPerPage: false,
                    selectAllRowsItem: false,
                    selectAllRowsItemText: t("All"),
                  }}
                  paginationDefaultPage={defaultPage}
                  paginationPerPage={perPage}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                />
              ) : (
                <></>
              )}
              <Confirmation
                showConfirmation={selectedEmployee !== null}
                onClose={() => setSelectedEmployee(null)}
                onConfirm={() => changeEmployeeState()}
                content={
                  selectedEmployee?.active === true
                    ? t("Czy na pewno chcesz zablokować użytkownika?")
                    : t("Czy na pewno chcesz odblokować użytkownika?")
                }
              />
              <Confirmation
                showConfirmation={selectedEmployeeDelete !== null}
                onClose={() => setSelectedEmployeeDelete(null)}
                onConfirm={() => deleteEmployee(selectedEmployeeDelete!!.id)}
                content={t("Czy na pewno chcesz usunąć użytkownika?")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
